import register from 'preact-custom-element'
import { useState, useRef, useEffect } from 'preact/hooks'
import { addClass, removeClass, selectAll } from 'helpers/dom'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { addCartItem } from 'libs/cart'
import useDetectBreakpoint from 'hooks/useDetectBreakpoint'
import useInCart from 'hooks/useInCart'
import Price from 'snippets/price/price'
import Icon from 'snippets/icon/icon'
import LoaderSpin from 'snippets/loader-spin/loader-spin'
import Image from 'snippets/image/image'
import useClickOutSide from 'hooks/useClickOutSide'
import './hot-spot.css'

function HotSpot ({ value = [], imageClass }) {
  const items = JSON.parse(value)
  const imageEls = selectAll(`.${imageClass}`)

  const [activeIndex, setActiveIndex] = useState(null)
  const [isAdding, setIsAdding] = useState(false)
  const { isMobile } = useDetectBreakpoint()
  const refSelector = useRef(null)

  const getContentStyle = (position) => {
    switch (position) {
      case 'top':
        return {
          left: '50%',
          transform: 'translate(-50%, calc(-50% - 20px))'
        }
      case 'right':
        return {
          top: '50%',
          transform: 'translate(calc(50% + 20px), -50%)'
        }
      case 'bottom':
        return {
          left: '50%',
          transform: 'translate(-50%, calc(50% + 20px))'
        }
      case 'left':
        return {
          top: '50%',
          transform: 'translate(calc(-50% - 20px), -50%)'
        }
    }
  }

  const classSizePrice = (variant) => {
    let classSize = 'text-sm'
    if (variant.compare_at_price && variant.compare_at_price !== 0) {
      const newClassSize = variant.compare_at_price > variant.price ? ' text-grey-300' : ' text-grey-700'
      classSize += newClassSize
    }
    return classSize
  }

  const handleClick = (index, e) => {
    imageEls.forEach(imageEl => removeClass('pointer-events-none', imageEl))
    setActiveIndex(index)

    if (isMobile) {
      globalEmitter.emit(emitterProps.product.hotSpot, items[index])
    }

    const slideHotSpot = e.target.closest('swiper-slide')
    const slideContainerHotSpot = e.target.closest('swiper-container')?.querySelectorAll('swiper-slide')

    if (slideHotSpot && slideContainerHotSpot) {
      slideContainerHotSpot.forEach(item => {
        item.style.position = null
        item.style.zIndex = null
      })
      slideHotSpot.style.position = 'relative'
      slideHotSpot.style.zIndex = '1'
    }
  }

  const addToCart = async (item) => {
    setIsAdding(true)
    const cartSingle = await addCartItem(item.id, {}, 1)
    if (cartSingle) globalEmitter.emit(emitterProps.cart.add, cartSingle)
    setIsAdding(false)
  }

  const handleOutSide = () => {
    setActiveIndex(null)
    imageEls.forEach(imageEl => addClass('pointer-events-none', imageEl))
  }

  const onHover = (index, e) => {
    if (!isMobile) handleClick(index, e)
  }

  const onLeave = () => {
    if (!isMobile) handleOutSide()
  }

  const isAvailable = (variant) => useInCart(variant).isAvailable

  refSelector.current && useClickOutSide(refSelector, () => {
    handleOutSide()
  })

  useEffect(() => {
    globalEmitter.on(emitterProps.modal.close, (element) => {
      if (element && element.dataset && element.dataset.target === 'hot-spot-modal') {
        handleOutSide()
      }
    })
  }, [])

  return (
    <div ref={refSelector}>
      {items.map((item, index) => (
        <div
          key={index}
          className={`absolute flex justify-center items-center w-10 h-10 ${activeIndex === index ? 'z-[2]' : 'z-[1]'}`}
          style={{
            left: `${isMobile ? item.coordinatesMobile.x : item.coordinates.x}%`,
            top: `${isMobile ? item.coordinatesMobile.y : item.coordinates.y}%`
          }}
          onMouseEnter={(e) => onHover(index, e)}
          onMouseLeave={onLeave}
        >
          <button
            type="button"
            title={`Preview ${item.product.title}`}
            onClick={(e) => handleClick(index, e)}
            className={`relative w-6 h-6 p-[5px] rounded-full transition-[background-color] duration-300 cursor-pointer ${activeIndex === index ? 'bg-white' : 'hotspot-ping'}`}
            aria-label={`Preview ${item.product.title}`}
          >
            <span className={`relative z-[2] block w-full h-full rounded-full transition-[background-color] duration-300 ${activeIndex === index ? 'bg-accent-2' : 'bg-white'}`} />
          </button>
          {!isMobile && activeIndex === index && (
            <div className="absolute p-4 bg-white rounded-sm flex flex-col items-center gap-4 w-[336px]" style={getContentStyle(item.position)}>
              <div className="flex items-center w-full">
                <a href={item.product.url} title={item.product.title} className="block w-20 h-20">
                  <Image
                    key={item.product.id}
                    imageUrl={[item.product.featured_image]}
                    className="aspect-1"
                    imageClass="fit"
                  />
                </a>
                <div className="flex flex-col flex-1 gap-1 pl-4 pr-6">
                  {!isAvailable(item.product.initVariant) &&
                    <p className="rounded bg-grey-200 px-1.5 py-0.5 text-xs font-medium text-grey-500 w-fit">
                      Sold Out
                    </p>
                  }
                  <a href={item.product.url} title={item.product.title} className="text-sm text-grey-1000 w-fit hover:underline">
                    {item.product.title}
                  </a>
                  <Price
                    className="flex flex-row-reverse gap-1 text-grey-700 w-fit"
                    classSize={classSizePrice(item.product.initVariant)}
                    price={item.product.initVariant.price}
                    originalPrice={item.product.initVariant.compare_at_price}
                    classColor="!text-error-content"
                  />
                </div>
                {isAdding ? (
                  <div className="relative ml-auto w-7 h-7">
                    <LoaderSpin width="7" height="7" spinClass="border-t-grey-1000 !border" />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => addToCart(item.product.initVariant)}
                    className={`w-7 h-7 p-[5px] border rounded-full ml-auto ${isAvailable(item.product.initVariant) ? 'border-grey-1000' : 'border-default pointer-events-none'}`}
                  >
                    <Icon viewBox="0 0 22 22" name="icon-bag-plus" className={`w-full h-full ${isAvailable(item.product.initVariant) ? 'text-grey-1000' : 'text-grey-500'}`} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default HotSpot

register(HotSpot, 'hot-spot')
